/**
 * Sprint 3 (Iter-220n) — Multi-Slot Events admin surface.
 *
 * Lets admins create ONE event with N independent shift slots — each with
 * its own date / time / role / pay rate / headcount — without duplicating
 * the parent event metadata. Slots can also be added, edited, or removed
 * after creation.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import {
  ArrowLeft, Plus, Loader2, RefreshCw, Calendar, Clock, Users,
  DollarSign, MapPin, Trash2, Sparkles, Building2, ChevronDown, ChevronUp,
  Layers, Pencil, X, CheckCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../utils/api';

const API = process.env.REACT_APP_BACKEND_URL;
const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const blankSlot = () => ({
  role_id: '',
  role_name: '',
  shift_date: '',
  start_time: '09:00',
  end_time: '17:00',
  talent_needed: 1,
  bill_rate: 30,
  talent_pay_rate: '',
  duties: '',
});

function ClientPicker({ value, label, onPick, onClear }) {
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  useEffect(() => {
    if (!q || q.length < 2) { setResults([]); return; }
    const t = setTimeout(async () => {
      try { const r = await api.get(`/admin/clients/search?q=${encodeURIComponent(q)}&limit=8`); setResults(r.data || []); }
      catch { setResults([]); }
    }, 250);
    return () => clearTimeout(t);
  }, [q]);
  if (value) {
    return (
      <div className="flex items-center gap-2 h-10 px-3 rounded-md border border-cyan-200 bg-cyan-50 text-sm">
        <Building2 className="w-4 h-4 text-cyan-700" />
        <span className="flex-1 truncate text-cyan-900" data-testid="event-client-selected">{label}</span>
        <button onClick={onClear} className="text-cyan-700 hover:text-cyan-900" data-testid="event-client-clear"><X className="w-4 h-4" /></button>
      </div>
    );
  }
  return (
    <div className="relative">
      <Input data-testid="event-client-search" value={q} onChange={e => setQ(e.target.value)} placeholder="Search client…" className="h-10" />
      {results.length > 0 && (
        <div className="absolute z-30 left-0 right-0 mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-56 overflow-auto">
          {results.map(c => (
            <button key={c.id} type="button" onClick={() => { onPick(c.id, c.display); setQ(''); setResults([]); }} className="w-full text-left px-3 py-2 text-sm hover:bg-cyan-50 border-b border-gray-100 last:border-b-0" data-testid={`client-result-${c.id}`}>
              <div className="font-medium text-gray-900 truncate">{c.display}</div>
              <div className="text-[11px] text-gray-500 truncate">{c.email}</div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function RolePicker({ value, label, onPick, testIdPrefix }) {
  const [roles, setRoles] = useState([]);
  useEffect(() => {
    (async () => {
      try {
        const r = await api.get('/roles');
        const data = Array.isArray(r.data) ? r.data : r.data?.roles || [];
        setRoles(data);
      } catch { /* ignore */ }
    })();
  }, []);
  return (
    <select
      data-testid={`${testIdPrefix}-role`}
      value={value || ''}
      onChange={e => {
        const r = roles.find(x => x.id === e.target.value);
        onPick(e.target.value, r?.name || '');
      }}
      className="h-9 px-2 text-sm rounded-md border border-gray-200 bg-white w-full"
    >
      <option value="">Select role…</option>
      {roles.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
    </select>
  );
}

export default function MultiSlotEvents() {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [eventDetail, setEventDetail] = useState({}); // series_id -> detail

  // Create form
  const [clientId, setClientId] = useState('');
  const [clientLabel, setClientLabel] = useState('');
  const [eventName, setEventName] = useState('');
  const [description, setDescription] = useState('');
  const [venueName, setVenueName] = useState('');
  const [venueAddress, setVenueAddress] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [slots, setSlots] = useState([blankSlot()]);
  const [saving, setSaving] = useState(false);

  // Add slot dialog
  const [addingSlotTo, setAddingSlotTo] = useState(null);
  const [newSlot, setNewSlot] = useState(blankSlot());

  const fetchEvents = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/admin/multi-slot-events?limit=100`, { headers: headers() });
      const d = await r.json();
      setEvents(d.events || []);
    } catch { toast.error('Failed to load events'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchEvents(); }, [fetchEvents]);

  const loadDetail = async (sid) => {
    try {
      const r = await fetch(`${API}/api/admin/multi-slot-events/${sid}`, { headers: headers() });
      const d = await r.json();
      setEventDetail(prev => ({ ...prev, [sid]: d }));
    } catch { toast.error('Failed to load event detail'); }
  };

  const toggleExpand = async (sid) => {
    const next = expandedId === sid ? null : sid;
    setExpandedId(next);
    if (next && !eventDetail[next]) await loadDetail(next);
  };

  const resetCreate = () => {
    setClientId(''); setClientLabel(''); setEventName(''); setDescription('');
    setVenueName(''); setVenueAddress(''); setCity(''); setState('');
    setSlots([blankSlot()]);
  };

  const submitCreate = async () => {
    if (!clientId) return toast.error('Select a client');
    if (!eventName.trim()) return toast.error('Event name is required');
    if (!venueName.trim() || !city.trim() || !state.trim()) return toast.error('Venue name, city and state are required');
    for (const [i, s] of slots.entries()) {
      if (!s.role_id) return toast.error(`Slot ${i + 1}: role is required`);
      if (!s.shift_date) return toast.error(`Slot ${i + 1}: date is required`);
      if (!s.start_time || !s.end_time) return toast.error(`Slot ${i + 1}: start/end time required`);
      if (!s.bill_rate) return toast.error(`Slot ${i + 1}: bill rate required`);
    }
    setSaving(true);
    try {
      const payload = {
        client_id: clientId,
        name: eventName.trim(),
        description: description.trim() || null,
        venue_name: venueName.trim(),
        venue_address: venueAddress.trim() || null,
        city: city.trim(),
        state: state.trim(),
        slots: slots.map(s => ({
          role_id: s.role_id,
          shift_date: s.shift_date,
          start_time: s.start_time,
          end_time: s.end_time,
          talent_needed: parseInt(s.talent_needed, 10) || 1,
          bill_rate: parseFloat(s.bill_rate) || 0,
          talent_pay_rate: s.talent_pay_rate ? parseFloat(s.talent_pay_rate) : null,
          duties: s.duties || null,
        })),
      };
      const r = await fetch(`${API}/api/admin/multi-slot-events`, {
        method: 'POST',
        headers: { ...headers(), 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const d = await r.json();
      if (r.ok) {
        toast.success(`Event created with ${d.slots_created} slot(s)`);
        setShowCreate(false);
        resetCreate();
        fetchEvents();
      } else toast.error(d.detail || 'Create failed');
    } catch { toast.error('Create failed'); }
    finally { setSaving(false); }
  };

  const addSlot = async () => {
    const s = newSlot;
    if (!s.role_id || !s.shift_date || !s.start_time || !s.end_time || !s.bill_rate) {
      return toast.error('Fill role, date, times and bill rate');
    }
    try {
      const r = await fetch(`${API}/api/admin/multi-slot-events/${addingSlotTo}/slots`, {
        method: 'POST',
        headers: { ...headers(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          role_id: s.role_id,
          shift_date: s.shift_date,
          start_time: s.start_time,
          end_time: s.end_time,
          talent_needed: parseInt(s.talent_needed, 10) || 1,
          bill_rate: parseFloat(s.bill_rate) || 0,
          talent_pay_rate: s.talent_pay_rate ? parseFloat(s.talent_pay_rate) : null,
          duties: s.duties || null,
        }),
      });
      const d = await r.json();
      if (r.ok) {
        toast.success('Slot added');
        setAddingSlotTo(null);
        setNewSlot(blankSlot());
        await loadDetail(d.series_id);
        fetchEvents();
      } else toast.error(d.detail || 'Add failed');
    } catch { toast.error('Add failed'); }
  };

  const deleteSlot = async (sid, shiftId) => {
    if (!window.confirm('Remove this slot? This cannot be undone.')) return;
    try {
      const r = await fetch(`${API}/api/admin/multi-slot-events/${sid}/slots/${shiftId}`, { method: 'DELETE', headers: headers() });
      const d = await r.json();
      if (r.ok) {
        toast.success('Slot removed');
        await loadDetail(sid);
        fetchEvents();
      } else toast.error(d.detail || 'Delete failed');
    } catch { toast.error('Delete failed'); }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-cyan-50" data-testid="multi-slot-events-page">
      <header className="bg-white border-b border-gray-200 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="icon" onClick={() => navigate('/admin/home')} data-testid="back-btn">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-base sm:text-lg font-semibold text-gray-900 flex items-center gap-2">
                <Layers className="w-5 h-5 text-cyan-500" /> Multi-Slot Events
              </h1>
              <p className="text-[11px] text-gray-500 hidden sm:block">One event, many independent shift slots — different times, roles, rates.</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={fetchEvents} data-testid="refresh-btn">
              <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
            </Button>
            <Button size="sm" onClick={() => setShowCreate(true)} className="bg-cyan-600 hover:bg-cyan-700 text-white" data-testid="create-event-btn">
              <Plus className="w-4 h-4 mr-1" /> New Multi-Slot Event
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-3 sm:px-6 py-4 sm:py-6 space-y-3">
        {loading ? (
          <div className="py-16 flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-cyan-500" /></div>
        ) : events.length === 0 ? (
          <Card className="border-gray-200">
            <CardContent className="py-16 text-center text-sm text-gray-500" data-testid="empty-state">
              <Layers className="w-10 h-10 mx-auto mb-2 text-gray-300" />
              No multi-slot events yet. Create one to bundle several shifts under a single parent event.
            </CardContent>
          </Card>
        ) : (
          events.map(ev => (
            <Card key={ev.series_id} className="border-gray-200" data-testid={`event-card-${ev.series_id}`}>
              <CardHeader className="py-3 px-4 cursor-pointer" onClick={() => toggleExpand(ev.series_id)}>
                <div className="flex items-center justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <CardTitle className="text-sm font-semibold text-gray-900 flex items-center gap-2 truncate">
                      <Sparkles className="w-4 h-4 text-cyan-500 shrink-0" /> {ev.name}
                    </CardTitle>
                    <p className="text-[11px] text-gray-500 mt-0.5 flex items-center gap-3 flex-wrap">
                      <span className="flex items-center gap-1"><Building2 className="w-3 h-3" /> {ev.client_name}</span>
                      <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {ev.first_date}{ev.last_date && ev.last_date !== ev.first_date ? ` → ${ev.last_date}` : ''}</span>
                      <span className="flex items-center gap-1"><Layers className="w-3 h-3" /> {ev.slot_count} slots</span>
                      <span className="flex items-center gap-1 text-emerald-600 font-medium"><DollarSign className="w-3 h-3" /> ${Math.round(ev.total_estimated_value).toLocaleString()}</span>
                    </p>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <Badge className="text-[10px] uppercase bg-cyan-100 text-cyan-800 border border-cyan-200">{ev.status}</Badge>
                    {expandedId === ev.series_id ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                  </div>
                </div>
              </CardHeader>
              {expandedId === ev.series_id && (
                <CardContent className="px-4 pb-4 pt-0" data-testid={`event-detail-${ev.series_id}`}>
                  {!eventDetail[ev.series_id] ? (
                    <div className="py-6 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-cyan-500" /></div>
                  ) : (
                    <>
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-[11px] uppercase tracking-wider text-gray-500">Shift Slots</p>
                        <Button size="sm" variant="outline" onClick={() => { setAddingSlotTo(ev.series_id); setNewSlot(blankSlot()); }} className="h-7 text-xs" data-testid={`add-slot-btn-${ev.series_id}`}>
                          <Plus className="w-3.5 h-3.5 mr-1" /> Add Slot
                        </Button>
                      </div>
                      <div className="space-y-2">
                        {eventDetail[ev.series_id].slots.map(s => (
                          <div key={s.shift_id} className="grid grid-cols-1 sm:grid-cols-[1fr_auto_auto_auto_auto_auto] gap-3 items-center bg-gray-50 rounded-md px-3 py-2 border border-gray-100" data-testid={`slot-row-${s.shift_id}`}>
                            <div className="min-w-0">
                              <p className="text-sm font-medium text-gray-900 truncate">{s.role_name || '—'}</p>
                              <p className="text-[11px] text-gray-500 truncate">{s.duties || s.venue_name}</p>
                            </div>
                            <span className="text-xs text-gray-700 flex items-center gap-1"><Calendar className="w-3 h-3 text-gray-400" />{s.shift_date}</span>
                            <span className="text-xs text-gray-700 flex items-center gap-1"><Clock className="w-3 h-3 text-gray-400" />{s.start_time}–{s.end_time}</span>
                            <span className="text-xs text-gray-700 flex items-center gap-1"><Users className="w-3 h-3 text-gray-400" />{s.filled_count}/{s.talent_needed}</span>
                            <span className="text-xs text-emerald-700 font-medium flex items-center gap-1"><DollarSign className="w-3 h-3" />${s.bill_rate}/hr</span>
                            <Button size="sm" variant="ghost" onClick={() => deleteSlot(ev.series_id, s.shift_id)} className="text-red-600 hover:bg-red-50 h-7 px-2" data-testid={`delete-slot-${s.shift_id}`}>
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </CardContent>
              )}
            </Card>
          ))
        )}
      </div>

      {/* Create dialog */}
      <Dialog open={showCreate} onOpenChange={(v) => { setShowCreate(v); if (!v) resetCreate(); }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="create-event-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Layers className="w-5 h-5 text-cyan-500" /> New Multi-Slot Event</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Client</Label>
                <ClientPicker value={clientId} label={clientLabel} onPick={(id, lbl) => { setClientId(id); setClientLabel(lbl); }} onClear={() => { setClientId(''); setClientLabel(''); }} />
              </div>
              <div>
                <Label className="text-xs">Event Name</Label>
                <Input data-testid="event-name" value={eventName} onChange={e => setEventName(e.target.value)} className="h-10" placeholder="e.g. Atlanta Convention Weekend" />
              </div>
              <div className="sm:col-span-2">
                <Label className="text-xs">Description (optional)</Label>
                <Input data-testid="event-description" value={description} onChange={e => setDescription(e.target.value)} className="h-10" placeholder="Internal notes…" />
              </div>
              <div>
                <Label className="text-xs">Venue Name</Label>
                <Input data-testid="event-venue-name" value={venueName} onChange={e => setVenueName(e.target.value)} className="h-10" />
              </div>
              <div>
                <Label className="text-xs">Venue Address</Label>
                <Input data-testid="event-venue-address" value={venueAddress} onChange={e => setVenueAddress(e.target.value)} className="h-10" />
              </div>
              <div>
                <Label className="text-xs">City</Label>
                <Input data-testid="event-city" value={city} onChange={e => setCity(e.target.value)} className="h-10" />
              </div>
              <div>
                <Label className="text-xs">State</Label>
                <Input data-testid="event-state" value={state} onChange={e => setState(e.target.value)} className="h-10" placeholder="GA" />
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-sm font-semibold text-gray-700">Shift Slots ({slots.length})</Label>
                <Button size="sm" variant="outline" onClick={() => setSlots(prev => [...prev, blankSlot()])} className="h-8 text-xs" data-testid="add-slot-create">
                  <Plus className="w-3.5 h-3.5 mr-1" /> Slot
                </Button>
              </div>
              {slots.map((s, i) => (
                <Card key={i} className="border-cyan-100 bg-cyan-50/30" data-testid={`create-slot-${i}`}>
                  <CardContent className="p-3 space-y-2">
                    <div className="flex items-center justify-between">
                      <p className="text-xs font-semibold text-cyan-800">Slot #{i + 1}</p>
                      {slots.length > 1 && (
                        <Button size="sm" variant="ghost" onClick={() => setSlots(prev => prev.filter((_, j) => j !== i))} className="h-7 text-red-600 hover:bg-red-50" data-testid={`remove-slot-${i}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      )}
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                      <div>
                        <Label className="text-[10px]">Role</Label>
                        <RolePicker value={s.role_id} onPick={(id, name) => setSlots(prev => prev.map((x, j) => j === i ? { ...x, role_id: id, role_name: name } : x))} testIdPrefix={`slot-${i}`} />
                      </div>
                      <div>
                        <Label className="text-[10px]">Date</Label>
                        <Input data-testid={`slot-${i}-date`} type="date" value={s.shift_date} onChange={e => setSlots(prev => prev.map((x, j) => j === i ? { ...x, shift_date: e.target.value } : x))} className="h-9" />
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <Label className="text-[10px]">Start</Label>
                          <Input data-testid={`slot-${i}-start`} type="time" value={s.start_time} onChange={e => setSlots(prev => prev.map((x, j) => j === i ? { ...x, start_time: e.target.value } : x))} className="h-9" />
                        </div>
                        <div>
                          <Label className="text-[10px]">End</Label>
                          <Input data-testid={`slot-${i}-end`} type="time" value={s.end_time} onChange={e => setSlots(prev => prev.map((x, j) => j === i ? { ...x, end_time: e.target.value } : x))} className="h-9" />
                        </div>
                      </div>
                      <div>
                        <Label className="text-[10px]">Talent Needed</Label>
                        <Input data-testid={`slot-${i}-talent`} type="number" min={1} value={s.talent_needed} onChange={e => setSlots(prev => prev.map((x, j) => j === i ? { ...x, talent_needed: e.target.value } : x))} className="h-9" />
                      </div>
                      <div>
                        <Label className="text-[10px]">Bill Rate ($/hr)</Label>
                        <Input data-testid={`slot-${i}-bill`} type="number" step="0.5" value={s.bill_rate} onChange={e => setSlots(prev => prev.map((x, j) => j === i ? { ...x, bill_rate: e.target.value } : x))} className="h-9" />
                      </div>
                      <div>
                        <Label className="text-[10px]">Talent Pay Rate (optional)</Label>
                        <Input data-testid={`slot-${i}-pay`} type="number" step="0.5" value={s.talent_pay_rate} onChange={e => setSlots(prev => prev.map((x, j) => j === i ? { ...x, talent_pay_rate: e.target.value } : x))} className="h-9" placeholder="Auto = 70% bill" />
                      </div>
                    </div>
                    <div>
                      <Label className="text-[10px]">Duties / Notes</Label>
                      <Input data-testid={`slot-${i}-duties`} value={s.duties} onChange={e => setSlots(prev => prev.map((x, j) => j === i ? { ...x, duties: e.target.value } : x))} className="h-9" placeholder="What the talent will be doing during this slot…" />
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowCreate(false)} data-testid="cancel-create">Cancel</Button>
            <Button onClick={submitCreate} disabled={saving} className="bg-cyan-600 hover:bg-cyan-700 text-white" data-testid="submit-create">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><CheckCircle className="w-4 h-4 mr-1" /> Create Event</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add slot dialog */}
      <Dialog open={!!addingSlotTo} onOpenChange={(v) => { if (!v) setAddingSlotTo(null); }}>
        <DialogContent className="max-w-lg" data-testid="add-slot-dialog">
          <DialogHeader>
            <DialogTitle>Add Slot</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <div className="grid grid-cols-2 gap-2">
              <div className="col-span-2">
                <Label className="text-[10px]">Role</Label>
                <RolePicker value={newSlot.role_id} onPick={(id, name) => setNewSlot(p => ({ ...p, role_id: id, role_name: name }))} testIdPrefix="newslot" />
              </div>
              <div>
                <Label className="text-[10px]">Date</Label>
                <Input data-testid="newslot-date" type="date" value={newSlot.shift_date} onChange={e => setNewSlot(p => ({ ...p, shift_date: e.target.value }))} className="h-9" />
              </div>
              <div>
                <Label className="text-[10px]">Talent Needed</Label>
                <Input data-testid="newslot-talent" type="number" min={1} value={newSlot.talent_needed} onChange={e => setNewSlot(p => ({ ...p, talent_needed: e.target.value }))} className="h-9" />
              </div>
              <div>
                <Label className="text-[10px]">Start</Label>
                <Input data-testid="newslot-start" type="time" value={newSlot.start_time} onChange={e => setNewSlot(p => ({ ...p, start_time: e.target.value }))} className="h-9" />
              </div>
              <div>
                <Label className="text-[10px]">End</Label>
                <Input data-testid="newslot-end" type="time" value={newSlot.end_time} onChange={e => setNewSlot(p => ({ ...p, end_time: e.target.value }))} className="h-9" />
              </div>
              <div>
                <Label className="text-[10px]">Bill Rate ($/hr)</Label>
                <Input data-testid="newslot-bill" type="number" step="0.5" value={newSlot.bill_rate} onChange={e => setNewSlot(p => ({ ...p, bill_rate: e.target.value }))} className="h-9" />
              </div>
              <div>
                <Label className="text-[10px]">Talent Pay Rate</Label>
                <Input data-testid="newslot-pay" type="number" step="0.5" value={newSlot.talent_pay_rate} onChange={e => setNewSlot(p => ({ ...p, talent_pay_rate: e.target.value }))} className="h-9" placeholder="Auto" />
              </div>
              <div className="col-span-2">
                <Label className="text-[10px]">Duties</Label>
                <Input data-testid="newslot-duties" value={newSlot.duties} onChange={e => setNewSlot(p => ({ ...p, duties: e.target.value }))} className="h-9" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setAddingSlotTo(null)}>Cancel</Button>
            <Button onClick={addSlot} className="bg-cyan-600 hover:bg-cyan-700 text-white" data-testid="submit-add-slot">
              <Plus className="w-4 h-4 mr-1" /> Add Slot
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
